/**
 * Shared types across the CRM. As we add features that need new shapes,
 * add them here so every part of the app sees the same definitions.
 */

/**
 * A single row from the `v_route_data` Supabase view.
 * Mirrors what the PowerShell dashboard already queries.
 *
 * Fields that come from LEFT JOINs are nullable.
 */
export interface Dispatch {
  dispatch: string;
  tech_empno: string;
  counter: number | null;
  appointment_date: string; // YYYY-MM-DD
  tpromtime: string | null; // HH:MM:SS
  window_code: string | null;
  status: string | null;
  tech_est_hours: number | null;
  notes: string | null;
  dispatch_est_min: number | null;
  multitech: string | null;
  dispatch_priority: string | null;
  custno: string;
  customer_first: string | null;
  customer_last: string | null;
  customer_fullname: string | null;
  customer_phone: string | null;
  locno: string | null;
  locname: string | null;
  service_address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  // Service-location coordinates (from v_route_data). ~87.7% populated across
  // the routing view; null where the address was never geocoded. Used by the
  // Board map + the SEG-01 route leg (BOARD-MAP-01 / ROUTE).
  loc_latitude: number | null;
  loc_longitude: number | null;
  tech_first: string | null;
  tech_last: string | null;
  tech_name: string | null;
  tech_dept: string | null;

  // ----- Pegasus-native fields (not from ESC mirror) -----
  // Populated by Pegasus pipelines once they land. All nullable for now.

  /**
   * Short AI-generated summary of the dispatch notes (30–45 chars).
   * Rendered on the DispatchBlock card with a ✨ prefix when present.
   * Falls back to extractJobPreview when null. See architecture-decisions §AI-03 / BACKLOG AI-03.
   */
  ai_summary: string | null;

  /**
   * AI-derived category for filters + reports + per-tech metrics.
   * One of: Tune-up | Repair | Diagnostic | Install | Callback |
   * Agreement-visit | Quote | Part-run | Multi | Other.
   * Not shown as a separate pill on the card (summary line carries the meaning).
   * See BACKLOG AI-03.
   */
  ai_category: string | null;

  /**
   * Workflow queue lane for pending / waiting dispatches (DEC-NEW-06 + DEC-NEW-01).
   * When set, `tech_empno` is logically empty (the row is in a lane, not assigned to a real tech).
   * Examples: 'intake', 'order-parts', 'weather-hold', 'callback', 'reschedule', 'serv-agr'.
   * Admin-managed taxonomy via BOARD-ADMIN-03.
   */
  queue_lane_id: string | null;

  /**
   * Per-dispatch billing party override (DEC-NEW-04 / CUST-08).
   * When set, billing goes to this Customer regardless of property ownership.
   * Use case: contractor (Acme Remodeling) pays for HVAC work on a homeowner's property.
   * When null, billing follows the Property's billing-party customer_property_link.
   */
  billing_customer_id: string | null;
}

/** Statuses that count as "done" on the board. */
export const STATUS_DONE = new Set(["Complete", "Done"]);

/** Statuses that count as "active right now". */
export const STATUS_ACTIVE = new Set(["Working", "Traveling"]);

/** Statuses that count as "still to come today" (assigned + scheduled). */
export const STATUS_PENDING = new Set(["Pending", "Dispatched"]);

/**
 * Statuses for Pegasus-native pending tickets (DEC-NEW-06).
 * A dispatch in this status has a queue_lane_id set and no tech_empno —
 * waiting to be scheduled (or waiting on parts / weather / callback / etc.).
 */
export const STATUS_PENDING_SCHEDULE = new Set(["pending_schedule"]);
